import { TrackedProps } from '@squareup/dex-types-shared-utils';
import { Box, Paragraph20, Paragraph30 } from '@squareup/dex-ui-shared-base';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { shallowEqual } from 'react-redux';

import { Link } from '../Link';

import styles from './navigation-item.module.css';

type NavigationItemProps = {
  href?: React.ComponentProps<typeof Link>['href'];
  text: string | ReactNode | undefined;
  testId: string | undefined;
  children?: ReactNode;
  isSubItem?: boolean;
  isActive: boolean;
  verticalMargin?: boolean;
} & TrackedProps;

interface NavigationProps {
  children?: ReactNode;
  subNavigation?: boolean;
}

function isHrefString(
  href: React.ComponentProps<typeof Link>['href']
): href is string {
  return typeof href === 'string';
}

const NavigationItem = React.memo(
  ({
    href,
    text,
    testId,
    children,
    isSubItem,
    isActive,
    trackingId,
    trackingExtra,
    verticalMargin = true,
  }: NavigationItemProps) => {
    return (
      <Box
        as={'li'}
        className={styles['list-item']}
        data-testid={testId}
        data-test-active={isActive}
        margin={{
          bottom: isSubItem || !verticalMargin ? undefined : '0.5x',
        }}
      >
        <>
          <Link
            href={href}
            trackingId={trackingId}
            trackingExtra={trackingExtra}
            className={clsx(
              isActive && styles.active,
              isSubItem && styles['sub-item'],
              styles['item-a']
            )}
          >
            {isSubItem ? (
              <Paragraph20
                padding={{
                  horizontal: '1x',
                  vertical: verticalMargin ? '0.5x' : undefined,
                }}
                border={{ radius: 'standard' }}
                className={clsx(styles.inner, isActive && styles.active)}
              >
                {text}
              </Paragraph20>
            ) : (
              <Paragraph30
                margin={{ vertical: verticalMargin ? '1x' : undefined }}
                className={clsx(styles.inner, isActive && styles.active)}
                weight="medium"
              >
                {text}
              </Paragraph30>
            )}
          </Link>
          {children}
        </>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    const { href: prevHref, text: prevText, ...prevPropsRest } = prevProps;
    const { href: nextHref, text: nextText, ...nextPropsRest } = nextProps;

    // On some pages, we have 1000s of navigation items.
    // To reduce the render time, we will memoize it, but we
    // have to do deeper equality checks on the objects like href and text

    if (!shallowEqual(prevPropsRest, nextPropsRest)) {
      return false;
    }

    if (prevText?.toString() !== nextText?.toString()) {
      return false;
    }

    // Types much match if undefined or a string
    if (
      prevHref === undefined ||
      nextHref === undefined ||
      isHrefString(prevHref) ||
      isHrefString(nextHref)
    ) {
      if (prevHref !== nextHref) {
        return false;
      }
    } else {
      if (prevHref.pathname !== nextHref.pathname) {
        return false;
      }

      if (JSON.stringify(prevHref.query) !== JSON.stringify(nextHref.query)) {
        return false;
      }
    }

    return true;
  }
);

NavigationItem.displayName = 'NavigationItem';

export { type NavigationItemProps, type NavigationProps, NavigationItem };
